/**
 * Runtime text-contrast enforcement.
 *
 * Reads the computed --bg-root and each text-* CSS variable from a wrapper
 * element. For any text variable that falls below its target WCAG ratio
 * against the background, it bumps the color toward white/black (preserving
 * hue) until the target is met, then sets the corrected value as an inline
 * style on the wrapper. Untouched values stay as-is, so theme designers can
 * still override the defaults — this only kicks in when the theme picks a
 * value that would actually be unreadable.
 *
 * Targets per slot:
 *   --text-heading   10:1   (AAA + slack)
 *   --text-body       8:1
 *   --text-secondary  6:1
 *   --text-muted      4.5:1 (WCAG AA for normal text)
 *   --text-faint      3:1   (WCAG AA for incidental / large text)
 */

const TARGETS = {
  "--text-heading": 10,
  "--text-body": 8,
  "--text-secondary": 6,
  "--text-muted": 4.5,
  "--text-faint": 3,
};

function srgbToLinear(channel) {
  const v = channel / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

function contrastRatio(a, b) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

function rgbToHex(r, g, b) {
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Parse a CSS color string into {r,g,b}. Handles #RGB, #RRGGBB, rgb(), rgba().
 * Returns null on failure.
 */
function parseColor(str) {
  if (!str) return null;
  str = str.trim();

  // #RRGGBB or #RGB
  if (str.startsWith("#")) {
    let hex = str.slice(1);
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }
    if (hex.length !== 6) return null;
    const num = parseInt(hex, 16);
    if (Number.isNaN(num)) return null;
    return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
  }

  // rgb(R, G, B) / rgba(R, G, B, A) — accepts both space- and comma-separated
  const m = str.match(/rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)/i);
  if (m) {
    return {
      r: Math.round(parseFloat(m[1])),
      g: Math.round(parseFloat(m[2])),
      b: Math.round(parseFloat(m[3])),
    };
  }

  return null;
}

/**
 * Push a color toward white (on dark bg) or black (on light bg) until it
 * meets the target contrast ratio. Binary search for the smallest tint amount.
 */
function ensureContrast(color, bg, targetRatio) {
  if (contrastRatio(color, bg) >= targetRatio) return color;

  const bgIsLight = relativeLuminance(bg) > 0.5;
  const toward = bgIsLight ? 0 : 255;

  let lo = 0;
  let hi = 1;
  let best = { ...color };
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2;
    const candidate = {
      r: Math.round(color.r + (toward - color.r) * mid),
      g: Math.round(color.g + (toward - color.g) * mid),
      b: Math.round(color.b + (toward - color.b) * mid),
    };
    if (contrastRatio(candidate, bg) >= targetRatio) {
      best = candidate;
      hi = mid;
    } else {
      lo = mid;
    }
  }
  return best;
}

/**
 * Apply WCAG-aware overrides to the given element. Reads its computed
 * --bg-root + text-* variables, and inline-styles any that don't meet target.
 *
 * Should be called *after* the data-theme attribute has been applied so the
 * computed values reflect the new theme.
 */
export function enforceThemeContrast(el) {
  if (!el) return;
  const styles = getComputedStyle(el);
  const bg = parseColor(styles.getPropertyValue("--bg-root"));
  if (!bg) return;

  for (const [varName, target] of Object.entries(TARGETS)) {
    const currentStr = styles.getPropertyValue(varName);
    const current = parseColor(currentStr);
    if (!current) continue;
    if (contrastRatio(current, bg) >= target) {
      // Already good — clear any inline override left from a previous theme
      el.style.removeProperty(varName);
      continue;
    }
    const adjusted = ensureContrast(current, bg, target);
    el.style.setProperty(
      varName,
      rgbToHex(adjusted.r, adjusted.g, adjusted.b)
    );
  }
}
